import './SettingsPanelItem.css';

import {
  CSSProperties,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import GridInput from '../../../GridInput/GridInput';
import FloatStepper, { FloatStepperHandle } from '../FloatStepper/FloatStepper';
import InputLabel from '../../../InputLabel/InputLabel';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useRequestDataContext } from '../../../../../hooks/context/useRequestDataContext';
import { useLayoutHandlersContext } from '../../../../../hooks/context/useLayoutHandlersContext';
import {
  withEditableResolve,
  WithEditableResolveProp,
} from '../../../../../hoc/withEditableResolve';
import {
  withVisiblityResolve,
  WithVisiblityResolveProp,
} from '../../../../../hoc/withVisiblityResolve';
import { useSettingsPanelItemHandlers } from '../../hooks/useSettingsPanelItemHandlers';
import {
  LAYOUT_INTEGER_FIELD_STEP,
  LAYOUT_SETTINGS_PANEL_ITEM_SPAN_FALLBACK,
} from '../../../../../constants';
import { FormApiMethodsRequestData } from '../../../../../api/types/types';
import {
  Dataset,
  FilterError,
  FilterType,
  Form,
  FormItem,
  FullscreenReferenceType,
  GridInputValue,
  JSONObject,
  LayoutInlineForm,
} from '../../../../../types/types';
import { LayoutHandlerScope } from '../../../../../types/context';

interface SettingsPanelItemProps {
  item: FormItem;
  value: GridInputValue;
  visible: boolean;
  editable: boolean;
  resolveData: JSONObject;
  formDefinition: LayoutInlineForm;
  formData: JSONObject;
  referenceDatasets: Record<string, Dataset>;
  setReferenceDatasets: Dispatch<SetStateAction<Record<string, Dataset>>>;
  isDisabled?: boolean;
  saveErrorKey?: string | null;
  handlerScope?: LayoutHandlerScope;
  onChange: (name: string, value: GridInputValue) => void;
  onApplyDefault?: (name: string, value: GridInputValue) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: (name: string) => void;
  onOpenFullscreenReference?: (reference: FullscreenReferenceType) => void;
}

const SettingsPanelItem: FC<SettingsPanelItemProps> = ({
  item,
  value,
  visible,
  editable,
  formDefinition,
  formData,
  referenceDatasets,
  setReferenceDatasets,
  isDisabled,
  saveErrorKey,
  handlerScope,
  onChange,
  onApplyDefault,
  onFieldFocus,
  onFieldBlur,
  onOpenFullscreenReference,
}) => {
  const [error, setError] = useState<FilterError>(null);
  const { t } = useTranslate();
  const stepperRef = useRef<FloatStepperHandle>(null);
  const { getFieldCallbacks } = useLayoutHandlersContext();
  const requestData = useRequestDataContext() as
    | FormApiMethodsRequestData
    | undefined;

  const name = item.name ?? '';

  const handlerCallbacks = handlerScope
    ? getFieldCallbacks(handlerScope, name, item.control, item.handlerTriggers)
    : {};
  const showLabel = item.type !== 'boolean';
  const isFieldDisabled =
    isDisabled || item.allowEditable === false || !editable;
  const hasNumericControl =
    !item.control ||
    item.control.type === 'number' ||
    item.control.type === 'integer';
  const showStepper =
    (item.type === 'float' || item.type === 'integer') &&
    hasNumericControl &&
    !isFieldDisabled;
  const displayedError = error ?? (saveErrorKey ? [t(saveErrorKey)] : null);

  const {
    handleChange,
    handleInputRowKeyDown,
    handleInputRowKeyUp,
    handleOpenFullscreenReference,
  } = useSettingsPanelItemHandlers({
    item,
    value,
    showStepper,
    stepperRef,
    handlerCallbacks,
    setError,
    onChange,
    onApplyDefault,
    onOpenFullscreenReference,
  });

  // Triggers without a handler scope would silently never fire — that's a
  // wiring bug of the hosting panel, so make it visible.
  useEffect(() => {
    if (!handlerScope && item.handlerTriggers?.length) {
      console.warn(
        `Field "${item.name}" has handlerTriggers but its SettingsPanel has no handlerScope`
      );
    }
  }, [handlerScope, item]);

  if (!name || !visible || item.allowVisible === false) {
    return null;
  }

  return (
    <div
      className="settings-panel-item"
      // Focus anchor of the shared focusFormFieldByName engine (lowercased by
      // its selector).
      data-form-field={name.toLowerCase()}
      style={
        {
          ['--form-item-span']:
            item.width ?? LAYOUT_SETTINGS_PANEL_ITEM_SPAN_FALLBACK,
        } as CSSProperties
      }
    >
      {showLabel && (
        <InputLabel hint={item.hint} className="settings-panel-item-label">
          {item.label || name}
          {item.mandatory && (
            <span className="settings-panel-item-required">*</span>
          )}
        </InputLabel>
      )}
      <div
        className="settings-panel-item-input-row"
        onKeyDown={handleInputRowKeyDown}
        onKeyUp={handleInputRowKeyUp}
        onFocusCapture={(): void => onFieldFocus?.(name)}
        // Focus leaving the row (blur fires before keyup lands elsewhere)
        // must not leave the auto-repeat running.
        onBlurCapture={(event): void => {
          stepperRef.current?.stopHold();

          // Focus moving inside the row (input ↔ stepper) is not a field leave.
          if (
            event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            return;
          }

          onFieldBlur?.(name);
        }}
      >
        <GridInput
          name={name}
          label={item.label}
          // FormItemType ⊃ FilterType ('binary'); picture fields render by
          // control.type, GridInput never branches on 'binary' itself.
          type={item.type as FilterType}
          value={value}
          control={item.control}
          disabled={isFieldDisabled}
          error={displayedError}
          setError={setError}
          onChange={handleChange}
          onTouch={handlerCallbacks.onTouch}
          onFocusIn={handlerCallbacks.onFocusIn}
          onFocusOut={handlerCallbacks.onFocusOut}
          referenceDatasets={referenceDatasets}
          setReferenceDatasets={setReferenceDatasets}
          isFormInput
          form={formDefinition as unknown as Form}
          formData={formData}
          requestData={requestData}
          openFullscreenReference={
            onOpenFullscreenReference
              ? handleOpenFullscreenReference
              : undefined
          }
        />
        {showStepper && (
          <FloatStepper
            ref={stepperRef}
            value={value}
            control={item.control}
            step={
              item.type === 'integer' ? LAYOUT_INTEGER_FIELD_STEP : undefined
            }
            onStep={handleChange}
          />
        )}
      </div>
    </div>
  );
};

export type SettingsPanelItemHOCProps = Omit<
  SettingsPanelItemProps,
  'visible' | 'editable'
> &
  WithVisiblityResolveProp &
  WithEditableResolveProp;

export default withVisiblityResolve<SettingsPanelItemHOCProps>(
  // The outer HOC has already resolved `visible` by the time this layer runs.
  withEditableResolve<
    Omit<SettingsPanelItemHOCProps, 'visible'> & { visible: boolean }
  >(SettingsPanelItem)
);
